import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@jhool-io/fe-components";
import { useDropzone } from "react-dropzone";
import { checkIfRichTextHasTextContent, cn } from "../../../../utils/helpers";
import {
    useAddInvoiceNoteDoc,
    useCreateInvoiceNote,
} from "../../../../hooks/mutations/billing";
import useToast from "../../../../hooks/useToast";
import { AddTextEditor } from "../../../../components/TextEditor/AddTextEditor/AddTextEditor";
import FolderOpenIcon from "../../../../components/Icons/FolderOpen";
import TimesIcon from "../../../../icons/Times";

interface AddInvoiceNoteFormProps {
    invoiceId: string;
    onFormSubmit: () => void;
}

export default function AddInvoiceNoteForm({
    invoiceId,
    onFormSubmit,
}: AddInvoiceNoteFormProps) {
    const [description, setDescription] = React.useState("");
    const [selectedFiles, setSelectedFiles] = React.useState<File[] | null>(
        null
    );

    const createInvoiceNote = useCreateInvoiceNote();
    const addInvoiceNoteDoc = useAddInvoiceNoteDoc();

    const formData = new FormData();

    const { toast } = useToast();
    const queryClient = useQueryClient();

    const onDrop = React.useCallback(
        (acceptedFiles: File[]) => {
            if (selectedFiles && selectedFiles.length > 0) {
                setSelectedFiles([...acceptedFiles, ...selectedFiles]);
            } else setSelectedFiles([...acceptedFiles]);
        },
        [selectedFiles]
    );

    const { getRootProps, isDragActive } = useDropzone({
        onDrop,
        maxFiles: 4,
        accept: {
            "application/pdf": [".pdf"],
            "image/*": [".png", ".webp", ".jpeg", ".jpg", ".avif"],
        },
    });

    // Function for setting selected files
    const handleSetSelectedFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            if (selectedFiles && selectedFiles.length > 0) {
                setSelectedFiles([...e.target.files, ...selectedFiles]);
            } else setSelectedFiles([...e.target.files]);
        }
    };

    const handleRemoveSelectedFile = (filename: string) => {
        if (selectedFiles) {
            const filesArr = [...selectedFiles].filter(
                (file) => file.name !== filename
            );
            if (filesArr.length === 0) setSelectedFiles(null);
            else setSelectedFiles(filesArr);
        }
    };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!checkIfRichTextHasTextContent(description)) {
            toast({ mode: "error", message: "Description is required" });
            return;
        }

        createInvoiceNote.mutate(
            { invoice_id: invoiceId, description },
            {
                onSuccess: (res) => {
                    toast({
                        mode: "success",
                        message:
                            res.message || "Internal note added successfully",
                    });

                    if (selectedFiles && selectedFiles.length > 0) {
                        for (let i = 0; i < [...selectedFiles].length; i += 1) {
                            formData.append("documents", [...selectedFiles][i]);
                        }
                        addInvoiceNoteDoc.mutate(
                            {
                                invoice_note_id: res.data.invoice_note_id,
                                formData,
                                invoice_id: invoiceId,
                            },
                            {
                                onSuccess: () => {
                                    queryClient.invalidateQueries({
                                        queryKey: ["invoice-notes", invoiceId],
                                    });
                                    toast({
                                        mode: "success",
                                        message:
                                            res.message ||
                                            "Internal note docs added successfully",
                                    });
                                    onFormSubmit();
                                    setDescription("");
                                },
                                onError: () => {
                                    toast({
                                        mode: "error",
                                        message:
                                            "Could not add documents to internal note",
                                    });
                                    onFormSubmit();
                                    setDescription("");
                                    queryClient.invalidateQueries({
                                        queryKey: ["invoice-notes", invoiceId],
                                    });
                                },
                            }
                        );
                    } else {
                        queryClient.invalidateQueries({
                            queryKey: ["invoice-notes", invoiceId],
                        });
                        setDescription("");
                        onFormSubmit();
                    }
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Could not add internal note at this time",
                    });
                },
            }
        );
    };

    return (
        <form id="add-internal-note" onSubmit={handleSubmit}>
            <div className="fg">
                <AddTextEditor
                    title="Enter internal note description"
                    onEditorTextChange={setDescription}
                    isRequiredFieldProvided
                    isRequired
                />
            </div>
            <p className="font-medium !text-caption text-textSec flex items-center gap-x-1 mb-1">
                Add file or files to upload
                <span className="text-textSec text-small" aria-label="optional">
                    (optional)
                </span>
            </p>
            <div
                className={cn(
                    "flex w-full items-center gap-x-[--spacing-items]  border border-dashed border-[--color-text-link] rounded-lg px-[--padding-card-x] py-[--padding-card-y]",
                    {
                        "border-[2px] border-dashed border-stroke":
                            isDragActive,
                    }
                )}
                {...getRootProps()}
            >
                <div className="p-16 bg-[rgba(204,250,233,0.50)] rounded-md shrink-0">
                    <FolderOpenIcon />
                </div>
                <div className="flex flex-col">
                    <div className="text-base font-medium">
                        <label
                            htmlFor="file-upload"
                            className="font-semibold text-textLink"
                        >
                            Click to upload{" "}
                            <input
                                className="w-full absolute left-[-99999px]"
                                id="file-upload"
                                name="file-upload"
                                type="file"
                                onChange={handleSetSelectedFiles}
                            />
                        </label>
                        or drag your file here
                    </div>
                    <p className="text-caption font-normal">
                        Maximum file size of 10 MB
                    </p>
                </div>
            </div>
            <div className="mt-[--spacing-form-items]">
                {selectedFiles &&
                    selectedFiles.map((file, i) => (
                        <div
                            key={`${file.name} - ${i + 1}`}
                            className="flex items-center justify-between w-full [border:1px_solid_var(--color-stroke)] rounded-md px-[--padding-card-x] py-[--padding-card-y] my-[--spacing-items]"
                        >
                            <div className="text-caption text-xs text-textSec">
                                {file.name}
                            </div>
                            <Button
                                variant="can-hover"
                                aria-label="delete icon"
                                size="auto"
                                onClick={() =>
                                    handleRemoveSelectedFile(file.name)
                                }
                            >
                                <TimesIcon />
                            </Button>
                        </div>
                    ))}
            </div>
        </form>
    );
}
